/**
 * PdfZoomPanWrap — mobile-first pinch-to-zoom + drag-to-pan wrapper for PDF canvas.
 *
 * Usage: replace <div className="pdf-canvas-wrap" onContextMenu={...}> with
 *   <PdfZoomPanWrap onContextMenu={...} resetKey={currentPage}>
 *
 * Features:
 *  - Pinch-to-zoom (two-finger) on touch screens
 *  - Mouse-wheel zoom (Ctrl+wheel or plain wheel)
 *  - Drag to pan when zoomed in (mouse or single-finger touch)
 *  - Double-tap to zoom 2×; double-tap again to reset
 *  - Floating +/−/% controls overlay
 *  - Resets when resetKey changes (e.g. page navigation)
 *  - Works in both normal and fullscreen mode
 */

import {
  useRef, useState, useCallback, useEffect,
  type ReactNode, type WheelEvent, type MouseEvent, type TouchEvent,
} from 'react';

const MIN_ZOOM  = 1.0;
const MAX_ZOOM  = 5.0;
const ZOOM_STEP = 0.25;

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

interface Props {
  children: ReactNode;
  onContextMenu?: (e: React.MouseEvent) => void;
  /** Reset zoom/pan when this value changes — pass currentPage */
  resetKey?: number | string;
}

export default function PdfZoomPanWrap({ children, onContextMenu, resetKey }: Props) {
  const wrapRef = useRef<HTMLDivElement>(null);

  // Use refs so event handlers always see fresh values without re-registering
  const zoomRef = useRef(1);
  const panXRef = useRef(0);
  const panYRef = useRef(0);

  // Trigger re-render for display
  const [view, setView] = useState({ zoom: 1, panX: 0, panY: 0 });

  const commit = useCallback((z: number, x: number, y: number) => {
    zoomRef.current = z;
    panXRef.current = x;
    panYRef.current = y;
    setView({ zoom: z, panX: x, panY: y });
  }, []);

  // Reset zoom/pan on page navigation
  useEffect(() => {
    commit(1, 0, 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  /** Compute max pan so canvas edge never moves past the wrap center */
  const maxPan = useCallback((z: number): number => {
    const w = wrapRef.current;
    if (!w) return 0;
    return Math.max(0, (z - 1) * Math.max(w.clientWidth, w.clientHeight) * 0.55);
  }, []);

  /**
   * Zoom to a specific screen zoom level, keeping screen point (sx, sy)
   * (relative to wrap top-left) fixed in view.
   * If sx/sy omitted, zooms around the wrap center.
   */
  const zoomAt = useCallback((newZoom: number, sx?: number, sy?: number) => {
    const z = clamp(newZoom, MIN_ZOOM, MAX_ZOOM);
    const wrap = wrapRef.current;
    if (!wrap) return;

    const cW = wrap.clientWidth;
    const cH = wrap.clientHeight;
    // Express focal point relative to wrap center (where transform-origin is)
    const rx = (sx ?? cW / 2) - cW / 2;
    const ry = (sy ?? cH / 2) - cH / 2;

    const ratio = z / zoomRef.current;
    let nx = z === 1 ? 0 : rx * (1 - ratio) + panXRef.current * ratio;
    let ny = z === 1 ? 0 : ry * (1 - ratio) + panYRef.current * ratio;

    const mp = maxPan(z);
    nx = clamp(nx, -mp, mp);
    ny = clamp(ny, -mp, mp);
    commit(z, nx, ny);
  }, [commit, maxPan]);

  const resetView = useCallback(() => commit(1, 0, 0), [commit]);

  // ── Mouse wheel zoom ────────────────────────────────────────────────────
  const handleWheel = useCallback((e: WheelEvent<HTMLDivElement>) => {
    e.preventDefault();
    const wrap = wrapRef.current;
    if (!wrap) return;
    const rect = wrap.getBoundingClientRect();
    const delta = e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP;
    zoomAt(zoomRef.current + delta, e.clientX - rect.left, e.clientY - rect.top);
  }, [zoomAt]);

  // ── Mouse drag pan ──────────────────────────────────────────────────────
  const dragRef = useRef<{ sx: number; sy: number; opx: number; opy: number } | null>(null);

  const handleMouseDown = useCallback((e: MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0 || zoomRef.current <= 1) return;
    e.preventDefault();
    dragRef.current = { sx: e.clientX, sy: e.clientY, opx: panXRef.current, opy: panYRef.current };
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.sx;
    const dy = e.clientY - dragRef.current.sy;
    const mp = maxPan(zoomRef.current);
    commit(
      zoomRef.current,
      clamp(dragRef.current.opx + dx, -mp, mp),
      clamp(dragRef.current.opy + dy, -mp, mp),
    );
  }, [commit, maxPan]);

  const handleMouseUp = useCallback(() => { dragRef.current = null; }, []);

  // ── Touch ───────────────────────────────────────────────────────────────
  const lastTouchRef   = useRef<{ x: number; y: number } | null>(null);
  const pinchRef       = useRef<{ dist: number; midX: number; midY: number } | null>(null);
  const lastTapTimeRef = useRef(0);

  const touchDist = (t1: React.Touch, t2: React.Touch) => {
    const dx = t1.clientX - t2.clientX;
    const dy = t1.clientY - t2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = useCallback((e: TouchEvent<HTMLDivElement>) => {
    const wrap = wrapRef.current;
    if (!wrap) return;

    if (e.touches.length === 1) {
      const t = e.touches[0];
      const now = Date.now();
      // Double-tap: toggle 2× zoom
      if (now - lastTapTimeRef.current < 300) {
        e.preventDefault();
        const rect = wrap.getBoundingClientRect();
        zoomRef.current > 1
          ? resetView()
          : zoomAt(2, t.clientX - rect.left, t.clientY - rect.top);
        lastTapTimeRef.current = 0;
        return;
      }
      lastTapTimeRef.current = now;
      lastTouchRef.current = { x: t.clientX, y: t.clientY };
      pinchRef.current = null;
    } else if (e.touches.length === 2) {
      e.preventDefault();
      const rect = wrap.getBoundingClientRect();
      const t1 = e.touches[0]; const t2 = e.touches[1];
      pinchRef.current = {
        dist: touchDist(t1, t2),
        midX: (t1.clientX + t2.clientX) / 2 - rect.left,
        midY: (t1.clientY + t2.clientY) / 2 - rect.top,
      };
      lastTouchRef.current = null;
    }
  }, [resetView, zoomAt]);

  const handleTouchMove = useCallback((e: TouchEvent<HTMLDivElement>) => {
    const wrap = wrapRef.current;
    if (!wrap) return;

    if (e.touches.length === 2 && pinchRef.current) {
      e.preventDefault();
      const rect  = wrap.getBoundingClientRect();
      const t1    = e.touches[0]; const t2 = e.touches[1];
      const dist  = touchDist(t1, t2);
      const midX  = (t1.clientX + t2.clientX) / 2 - rect.left;
      const midY  = (t1.clientY + t2.clientY) / 2 - rect.top;
      const cW    = wrap.clientWidth; const cH = wrap.clientHeight;

      const scaleRatio = dist / pinchRef.current.dist;
      const newZ   = clamp(zoomRef.current * scaleRatio, MIN_ZOOM, MAX_ZOOM);
      const ratio  = newZ / zoomRef.current;
      const rx     = midX - cW / 2; const ry = midY - cH / 2;
      const mp     = maxPan(newZ);
      const nx     = clamp(rx * (1 - ratio) + panXRef.current * ratio, -mp, mp);
      const ny     = clamp(ry * (1 - ratio) + panYRef.current * ratio, -mp, mp);
      commit(newZ, nx, ny);
      pinchRef.current = { dist, midX, midY };

    } else if (e.touches.length === 1 && lastTouchRef.current) {
      if (zoomRef.current <= 1) return; // allow native scroll when not zoomed
      e.preventDefault();
      const dx  = e.touches[0].clientX - lastTouchRef.current.x;
      const dy  = e.touches[0].clientY - lastTouchRef.current.y;
      lastTouchRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      const mp  = maxPan(zoomRef.current);
      commit(zoomRef.current, clamp(panXRef.current + dx, -mp, mp), clamp(panYRef.current + dy, -mp, mp));
    }
  }, [commit, maxPan]);

  const handleTouchEnd = useCallback(() => {
    pinchRef.current      = null;
    lastTouchRef.current  = null;
  }, []);

  // ── Render ──────────────────────────────────────────────────────────────
  const { zoom, panX, panY } = view;
  const isZoomed   = zoom > 1.005;
  const zoomPct    = Math.round(zoom * 100);
  // scale(z) translate(px/z, py/z) ≡ apply translate in content space then scale
  // Result: canvas center shifts by (panX, panY) in screen pixels
  const layerStyle = {
    transform:       `scale(${zoom}) translate(${panX / zoom}px, ${panY / zoom}px)`,
    transformOrigin: 'center center',
    willChange:      'transform',
    display:         'flex',
    alignItems:      'center',
    justifyContent:  'center',
    width:           '100%',
  } as const;

  return (
    <div
      ref={wrapRef}
      className={`pdf-canvas-wrap${isZoomed ? ' pdf-canvas-wrap--zoomed' : ''}`}
      onContextMenu={onContextMenu}
      onWheel={handleWheel}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div style={layerStyle}>
        {children}
      </div>

      {/* Floating zoom controls — always visible over the canvas */}
      <div className="pdf-zoom-controls" role="group" aria-label="Zoom controls">
        <button
          className="pdf-zoom-btn"
          onClick={() => zoomAt(zoom - ZOOM_STEP)}
          disabled={zoom <= MIN_ZOOM}
          aria-label="Zoom out"
          title="Zoom out (−)"
        >−</button>
        <button
          className="pdf-zoom-reset"
          onClick={resetView}
          aria-label={`${zoomPct}% — tap to reset zoom`}
          title="Reset zoom"
        >{zoomPct}%</button>
        <button
          className="pdf-zoom-btn"
          onClick={() => zoomAt(zoom + ZOOM_STEP)}
          disabled={zoom >= MAX_ZOOM}
          aria-label="Zoom in"
          title="Zoom in (+)"
        >+</button>
      </div>

      {/* Hint — visible only at 100% zoom */}
      {!isZoomed && (
        <div className="pdf-zoom-hint" aria-hidden="true">
          Pinch or scroll to zoom · Double-tap to zoom
        </div>
      )}
    </div>
  );
}
